'use strict';

/**
 * Plain-language employee profile summaries — one flowing paragraph, no section labels.
 */

const { formatPersonName } = require('./formatPersonName');
const { simplifyCopy } = require('./simpleCopy');
const { POSITIVE_STATUS } = require('./insightEligibility');

function finalizeProfileSummary(text) {
  return simplifyCopy(text, { maxWordsPerSentence: 45 });
}

function riskLevelFromScore(score) {
  const s = Math.round(Number(score) || 0);
  if (s <= 24) return 'low';
  if (s <= 49) return 'moderate';
  if (s <= 74) return 'high';
  return 'critical';
}

function indefiniteArticle(word) {
  const w = String(word || '').trim();
  if (!w) return 'a';
  return /^[aeiou]/i.test(w) ? 'an' : 'a';
}

function roleLabel(role) {
  const r = String(role || '').trim();
  return r || 'employee';
}

function subjectIntro(employee) {
  const name = formatPersonName(employee?.name);
  const role = roleLabel(employee?.role);
  if (name) return `${name} is ${indefiniteArticle(role)} ${role}`;
  return `This employee is ${indefiniteArticle(role)} ${role}`;
}

function riskLevelPhrase(level) {
  const l = String(level || '').toLowerCase();
  if (l === 'critical' || l === 'high') return 'a high risk of leaving';
  if (l === 'moderate') return 'a moderate risk of leaving';
  return 'a low risk of leaving';
}

function roundPct(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return Math.round(v);
}

function parsePctFromText(text) {
  const m = String(text || '').match(/(\d+(?:\.\d+)?)\s*%/);
  return m ? roundPct(parseFloat(m[1], 10)) : null;
}

function lowercaseFirst(text) {
  const t = String(text || '').trim();
  if (!t) return '';
  return t.charAt(0).toLowerCase() + t.slice(1);
}

function capitalizeFirst(text) {
  const t = String(text || '').trim();
  if (!t) return '';
  return t.charAt(0).toUpperCase() + t.slice(1);
}

const SKIP_SUMMARY_FACTOR_KEYS = new Set([
  'tenure_band',
  'high_risk_cluster_density',
]);

const DRIVER_PRIORITY = [
  'salary_vs_market_midpoint',
  'salary_vs_internal_peers',
  'external_demand_for_role',
  'competitor_talent_pull',
  'local_market_hiring_pressure',
  'manager_change_frequency',
  'time_since_last_promotion',
  'workload_pressure',
  'team_workload_pressure',
  'team_attrition_rate',
  'team_attrition_trend',
  'total_company_tenure',
  'time_in_current_role',
  'promotion_path_clarity',
  'internal_mobility_availability',
];

const TECHNICAL_CLAUSE_RE =
  /\b(discrete band|tenure band|leading retention signal|modeled risk|weighted|index \d|lifecycle band|factor score)\b/i;

function driverPriority(factorKey) {
  const idx = DRIVER_PRIORITY.indexOf(factorKey);
  return idx === -1 ? DRIVER_PRIORITY.length + 1 : idx;
}

function orderedDrivers(primary, secondary) {
  return [primary, ...(secondary || [])]
    .filter(Boolean)
    .filter((d) => !SKIP_SUMMARY_FACTOR_KEYS.has(d.factorKey))
    .sort((a, b) => driverPriority(a.factorKey) - driverPriority(b.factorKey));
}

function isExternalDemandDriver(driver) {
  const key = driver?.factorKey;
  const detail = String(driver?.detail || '');
  return (
    key === 'external_demand_for_role' ||
    key === 'competitor_talent_pull' ||
    key === 'local_market_hiring_pressure' ||
    /outside hiring demand|outside demand for|strong outside/i.test(detail)
  );
}

function isPayRelatedDriver(driver) {
  const key = driver?.factorKey;
  const detail = String(driver?.detail || '').toLowerCase();
  return (
    key === 'salary_vs_market_midpoint' ||
    key === 'salary_vs_internal_peers' ||
    key === 'compensation_growth_rate' ||
    key === 'high_risk_cluster_density' ||
    /below market|under market|below the median|peer median|compensation|pay is|pay being/i.test(detail)
  );
}

/** Convert a structured driver detail into a short clause for "due to …". */
function driverClause(driver) {
  if (!driver) return null;
  const detail = String(driver.detail || '').trim();
  const key = driver.factorKey || null;
  if (!detail) return null;
  if (key === 'total_company_tenure') {
    const ym = detail.match(/(\d+(?:\.\d+)?)\s+years/i);
    if (ym && parseFloat(ym[1], 10) >= 5) {
      return `about ${Math.round(parseFloat(ym[1], 10))} years with the company`;
    }
    return null;
  }

  if (TECHNICAL_CLAUSE_RE.test(detail)) return null;

  if (isExternalDemandDriver(driver)) return null;

  if (key === 'salary_vs_market_midpoint' || /below market|under market|below the market/i.test(detail)) {
    const pct = parsePctFromText(detail);
    if (pct != null && pct > 0) return `pay being about ${pct}% below market`;
    if (/below market|under market/i.test(detail)) return 'pay being below market';
  }

  if (key === 'salary_vs_internal_peers' || /below the median for peers/i.test(detail)) {
    const pct = parsePctFromText(detail);
    if (pct != null) return `roughly ${pct}% below the median for similar roles in the same department`;
    return 'pay being below typical levels for similar roles in the same department';
  }

  if (key === 'manager_change_frequency' || /manager change/i.test(detail)) {
    const m = detail.match(/(\d+)\s+manager/i);
    if (m) return `${m[1]} recent manager changes creating instability`;
    return 'recent manager changes creating instability';
  }

  if (key === 'time_since_last_promotion' || /promotion|growth feels stalled/i.test(detail)) {
    const ym = detail.match(/about (\d+(?:\.\d+)?) years ago/i);
    if (ym) return `not being promoted for about ${Math.round(parseFloat(ym[1], 10))} years`;
    return 'limited promotion progress';
  }

  if (key === 'workload_pressure' || key === 'team_workload_pressure' || /workload pressure/i.test(detail)) {
    return 'sustained workload pressure';
  }

  if (key === 'team_attrition_rate' || key === 'team_attrition_trend' || /team turnover/i.test(detail)) {
    return 'elevated turnover on their team';
  }

  if (key === 'high_risk_cluster_density' && /below market/i.test(detail)) {
    const pct = parsePctFromText(detail);
    if (pct != null) return `pay being about ${pct}% below market`;
    return 'pay being below market across the team';
  }

  let simplified = detail
    .replace(/Address compensation before other retention steps\.?/gi, '')
    .replace(/\(demand index \d+\)/gi, '')
    .replace(/demand index \d+/gi, '')
    .replace(/\(\d+\/100\)/g, '')
    .replace(/ for [A-Z][^.]{8,}\.$/i, '')
    .replace(/\.\s*$/, '')
    .trim();

  if (/^pay is /i.test(simplified)) {
    simplified = simplified.replace(/^pay is /i, 'pay being ');
  }

  if (simplified.length > 90) simplified = simplified.split(/[.;]/)[0].trim();
  if (simplified.length < 12 || TECHNICAL_CLAUSE_RE.test(simplified)) return null;
  return lowercaseFirst(simplified);
}

function externalDemandSentence(employee, drivers) {
  for (const d of drivers) {
    if (!isExternalDemandDriver(d)) continue;
    const detail = String(d?.detail || '');
    const role = roleLabel(employee?.role).toLowerCase();
    const rolePlural = role.endsWith('s') ? role : `${role}s`;
    if (/strong|elevated|high|active/i.test(detail)) {
      return `Strong outside demand for ${rolePlural} makes it easy for competitors to recruit them`;
    }
    return `Outside hiring is active for ${rolePlural}, which adds recruiting pressure`;
  }
  return null;
}

function retentionPriority(primary, reasonClauses) {
  const key = primary?.factorKey;
  const blob = `${primary?.detail || ''} ${reasonClauses.join(' ')}`.toLowerCase();

  if (
    key === 'salary_vs_market_midpoint' ||
    key === 'salary_vs_internal_peers' ||
    key === 'compensation_growth_rate' ||
    isPayRelatedDriver(primary) ||
    /pay|compensation|market|below market/i.test(blob)
  ) {
    return 'improving compensation should be the top priority for retention';
  }
  if (
    key === 'manager_change_frequency' ||
    key === 'manager_tenure' ||
    key === 'manager_team_attrition_history' ||
    /manager/i.test(blob)
  ) {
    return 'stabilizing manager support should be the top priority for retention';
  }
  if (
    key === 'time_since_last_promotion' ||
    key === 'career_velocity_vs_peers' ||
    key === 'promotion_path_clarity' ||
    key === 'internal_mobility_availability' ||
    /promotion|growth path|career/i.test(blob)
  ) {
    return 'clarifying their growth path should be the top priority for retention';
  }
  if (key === 'workload_pressure' || key === 'team_workload_pressure' || /workload/i.test(blob)) {
    return 'reducing workload pressure should be the top priority for retention';
  }
  if (key === 'team_attrition_rate' || key === 'team_attrition_trend') {
    return 'addressing team stability should be the top priority for retention';
  }
  return 'addressing the main concern should be the top priority for retention';
}

function joinClauses(clauses) {
  const unique = [];
  const seen = new Set();
  for (const c of clauses) {
    const t = String(c || '').trim();
    if (!t) continue;
    const norm = t.toLowerCase();
    if (seen.has(norm)) continue;
    seen.add(norm);
    unique.push(t);
  }
  if (unique.length === 0) return '';
  if (unique.length === 1) return unique[0];
  if (unique.length === 2) return `${unique[0]} and ${unique[1]}`;
  return `${unique.slice(0, -1).join(', ')}, and ${unique[unique.length - 1]}`;
}

function positiveAnchor(positiveSignals) {
  for (const p of positiveSignals || []) {
    const detail = String(p?.detail || p?.text || '').trim();
    if (!detail) continue;
    if (/demand index|outside hiring demand|outside demand/i.test(detail)) continue;
    return detail.replace(/\.$/, '');
  }
  return null;
}

/**
 * @param {{
 *   employee?: object|null,
 *   score?: number|null,
 *   level?: string|null,
 *   primary?: object|null,
 *   secondary?: object[],
 *   positiveSignals?: object[],
 * }} params
 * @returns {string}
 */
function buildNaturalProfileSummary({
  employee = null,
  score = null,
  level = null,
  primary = null,
  secondary = [],
  positiveSignals = [],
}) {
  const lvl = level || riskLevelFromScore(score);
  const intro = subjectIntro(employee);

  if (lvl === 'low') {
    const anchor =
      positiveAnchor(positiveSignals) ||
      POSITIVE_STATUS.STABLE_OUTLOOK ||
      'retention looks stable from available data';
    return finalizeProfileSummary(
      `${intro} with a low risk of leaving. ${capitalizeFirst(lowercaseFirst(anchor))}.`
    );
  }

  const allDrivers = orderedDrivers(primary, secondary);
  const reasonClauses = [];
  for (const d of allDrivers) {
    if (isExternalDemandDriver(d)) continue;
    const clause = driverClause(d);
    if (!clause) continue;
    if (!reasonClauses.some((r) => r.toLowerCase() === clause.toLowerCase())) {
      reasonClauses.push(clause);
    }
    if (reasonClauses.length >= 2) break;
  }

  let summary = `${intro} with ${riskLevelPhrase(lvl)}`;
  if (reasonClauses.length > 0) {
    summary += ` due to ${joinClauses(reasonClauses)}`;
  }
  summary += '.';

  const demand = externalDemandSentence(employee, allDrivers);
  const priority = retentionPriority(primary, reasonClauses);

  if (demand) {
    summary += ` ${demand}, so ${priority}.`;
  } else if (reasonClauses.length > 0) {
    summary += ` ${capitalizeFirst(priority)}.`;
  } else if (primary?.detail) {
    const fallback = driverClause(primary) || lowercaseFirst(String(primary.detail).replace(/\.$/, ''));
    summary += fallback ? ` due to ${fallback}. ${capitalizeFirst(priority)}.` : ` ${capitalizeFirst(priority)}.`;
  }

  return finalizeProfileSummary(summary);
}

/** Strip legacy section labels from persisted summaries when structured data is unavailable. */
function polishLegacyProfileSummary(text) {
  let out = String(text || '').trim();
  if (!out) return '';
  if (!/Risk of leaving is|Other factors:|Strengths that help retention:|Only \d+% of factors|\(\d+\/100\)/i.test(out)) {
    return out;
  }
  out = out
    .replace(/Risk of leaving is \w+ \(\d+\/100\) for [^.]+\.\s*/gi, '')
    .replace(/Risk of leaving is \w+ for [^.]+\.\s*/gi, '')
    .replace(/Other factors:\s*/gi, '')
    .replace(/Strengths that help retention:\s*/gi, '')
    .replace(/Only \d+% of factors had data\.[^.]*\.?\s*/gi, '')
    .replace(/Add more HR details for a fuller picture\.?\s*/gi, '')
    .replace(/Address compensation before other retention steps\.?\s*/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return finalizeProfileSummary(out);
}

module.exports = {
  buildNaturalProfileSummary,
  polishLegacyProfileSummary,
  driverClause,
  riskLevelPhrase,
  subjectIntro,
  riskLevelFromScore,
};
